import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus } from "@/components/ui/icon";

import type { Process } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/app/StatusBadge";
import { formatDateTime } from "@/lib/format";
import { humanize } from "@/resources/projects/_form";
import { RowActions } from "@/resources/_shared/ListScaffold";
import { ResourceTable } from "../skills/_components/ResourceTable";

/**
 * Processes (contours) list — the shared `ResourceTable` (PageHeader + table +
 * canonical states). No id column: name leads, then domain / artifact / rigidity
 * / status / updated, with the `…` overflow for View · Edit.
 */
export function ProcessList() {
  const { create, edit, show } = useNavigation();

  const columns = useMemo<ColumnDef<Process>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ getValue }) => (
          <span className="font-medium">{getValue() as string}</span>
        ),
      },
      {
        id: "domain",
        accessorKey: "domain",
        header: "Domain",
        cell: ({ getValue }) => (getValue() as string | null) ?? "—",
      },
      {
        id: "artifact_type",
        accessorKey: "artifact_type",
        header: "Artifact",
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? humanize(v) : "—";
        },
      },
      {
        id: "rigidity_level",
        accessorKey: "rigidity_level",
        header: "Rigidity",
        cell: ({ getValue }) => (getValue() as number | null) ?? "—",
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">
            {formatDateTime(getValue() as string)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <RowActions
              onShow={() => show("processes", row.original.id)}
              onEdit={() => edit("processes", row.original.id)}
            />
          </div>
        ),
      },
    ],
    [show, edit],
  );

  return (
    <ResourceTable<Process>
      resource="processes"
      title="Processes"
      subtitle="Contour definitions — their phases, ordering, and transition rules."
      columns={columns}
      initialSort={{ field: "updated_at", order: "desc" }}
      emptyMessage="No processes yet."
      toolbar={
        <Button onClick={() => create("processes")}>
          <Plus className="mr-2 h-4 w-4" />
          New Process
        </Button>
      }
    />
  );
}

export default ProcessList;
